import { http, http_v2, http_v3 } from '@api/client';
import { paramterize, pickResponse, pickResult } from '@api/utils';
import { CompanyMappingDetail } from '@lib/company-mapping';
import { HoldingDataResult, HoldingDataFilterParams, FilterResponse } from './types';
import {
  GVDSourceDataDataResult,
  GVDSourceDataFilterParams,
  FilterResponseGVDSourceData,
} from './types';
import { HoldingDataDetail } from '@lib/holding-data';

/**
 * Get the C2C details from the server.
 * @param id The id of the C2C to get
 */
export function get(vtr_id: string) {
  return http_v2.get(`/gvd-holding-data/${vtr_id}`).pipe(pickResult());
}

/**
 * Filter deals using dynamic operators.
 * @param option Query options
 */
export function filter({
  filters = [],
  pageSize: page_size = 10,
  page = 1,
  sortBy = [],
}: HoldingDataFilterParams = {}) {
  const sort = sortBy.map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`).join(';');
  const filtersMap: Record<string, string> = filters.reduce(
    (map, f) =>
      Object.assign(map, {
        [f.id]: Object.entries(f.value)
          .map(([op, v]) => (op === '$bw' ? `$gte|||${v[0]};$lte|||${v[1]}` : `${op}|||${v}`))
          .join(';'),
      }),
    {}
  );

  const params = paramterize({ page_size, page, sort, ...filtersMap });

  return http_v2.get<FilterResponse>('/gvd-holding-data', { params }).pipe(pickResponse());
}

/**
 * Updates the given C2C.
 * @param C2C The C2C to update
 */
export function update(holdingData: Partial<HoldingDataDetail>) {
  return http
    .put<HoldingDataDetail>(`/gvd-holding-data/${holdingData.vtr_id}`, holdingData)
    .pipe(pickResponse());
}

/**
 * Deletes the given C2C.
 * @param C2C The C2C id or the actual C2C.
 */
export function remove(companymapping: Partial<CompanyMappingDetail>) {
  return http_v2
    .delete<HoldingDataResult>(
      `/company-company-relation/${companymapping.company1.vtr_id}?relation_name=${companymapping.relation_name}`,
      {
        data: companymapping,
      }
    )
    .pipe(pickResponse());
}

/**
 * Creates the given C2C.
 * @param C2C The C2C to create
 */
export function create(company: CompanyMappingDetail) {
  return http_v2.post<HoldingDataResult>(`/company-company-relation`, company).pipe(pickResponse());
}

/**
 * Get Source Data of GVD
 */
export function gvdSourceDataFilter({
  filters = [],
  pageSize: page_size = 10,
  page = 1,
  sortBy = [],
}: GVDSourceDataFilterParams = {}) {
  const sort = sortBy.map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`).join(';');
  const filtersMap: Record<string, string> = filters.reduce(
    (map, f) =>
      Object.assign(map, {
        [f.id]: Object.entries(f.value)
          .map(([op, v]) => (op === '$bw' ? `$gte|||${v[0]};$lte|||${v[1]}` : `${op}|||${v}`))
          .join(';'),
      }),
    {}
  );

  const params = paramterize({ page_size, page, sort, ...filtersMap });

  return http_v2
    .get<FilterResponseGVDSourceData>('/gvd-source-data', { params })
    .pipe(pickResponse());
}

/**
 * Creates the given third_party_program_root_id's mapped content.
 * @param third_party_program_root_id The third_party_program_root_id details content to create
 */
export function createThirdPartyContent(third_party_program_root_id: string) {
  return http
    .post<HoldingDataResult>(`/thirdparty_content`, {
      program_root_id: third_party_program_root_id,
    })
    .pipe(pickResponse());
}
